import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  Users,
  LogOut,
  Menu,
  X,
  TrendingUp,
  Settings,
  Bell,
  ChevronDown,
  User,
  Shield,
  Sun,
  Moon,
  Database,
  Globe,
  Plus,
  Edit
} from 'lucide-react';
import logo from '../../images/logo/logo.jpeg';
import Modal from '../Modal/Modal';
import AdminProfile from '../../pages/Admin/AdminProfile';

const AdminLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();

  // Dynamic page header configuration
  const getPageHeaderConfig = () => {
    const pageConfigs = {
      '/admin': { icon: BarChart3, title: 'Dashboard', subtitle: 'Overview of system performance and metrics' },
      '/admin/leads': { icon: Users, title: 'Lead Management', subtitle: 'Manage and track all leads' },
      '/admin/leads/new': { icon: Plus, title: 'Create New Lead', subtitle: 'Add a new lead to the system' },
      '/admin/master-records': { icon: Database, title: 'Master Records', subtitle: 'Manage all system master data' },
      '/admin/master-records/website': { icon: Globe, title: 'Website Master Records', subtitle: 'Manage website-specific master data' },
      '/admin/master-records/admin-portal': { icon: Settings, title: 'Admin Portal Master Records', subtitle: 'Manage admin portal-specific master data' },
      '/admin/analytics': { icon: TrendingUp, title: 'Analytics', subtitle: 'Detailed reports and insights' },
      '/admin/settings': { icon: Settings, title: 'Settings', subtitle: 'Configure system settings' },
    };

    // Check for exact match first
    if (pageConfigs[location.pathname]) {
      return pageConfigs[location.pathname];
    }
    
    // Check for dynamic routes (like edit lead)
    if (location.pathname.startsWith('/admin/leads/') && location.pathname !== '/admin/leads/new') {
      const leadId = location.pathname.split('/').pop();
      if (leadId && leadId !== 'new') {
        return { icon: Edit, title: 'Edit Lead', subtitle: `Modify lead #${leadId}` };
      }
    }
    
    // Default fallback
    return { icon: Settings, title: 'Admin Portal', subtitle: 'System administration' };
  };

  const pageHeaderConfig = getPageHeaderConfig();
  const HeaderIcon = pageHeaderConfig.icon;

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: BarChart3 },
    { name: 'Leads', href: '/admin/leads', icon: Users },
    { 
      name: 'Master Records', 
      icon: Database, 
      hasSubmenu: true,
      submenu: [
        { name: 'Website Master', href: '/admin/master-records/website', icon: Database },
        { name: 'Admin Portal Master', href: '/admin/master-records/admin-portal', icon: Settings }
      ]
    },
    { name: 'Analytics', href: '/admin/analytics', icon: TrendingUp },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ];

  const sidebarVariants = {
    open: { x: 0, transition: { type: "spring", stiffness: 300, damping: 30 } },
    closed: { x: "-100%", transition: { type: "spring", stiffness: 300, damping: 30 } }
  };

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const handleProfileClick = () => {
    setShowProfileModal(true);
    setProfileDropdownOpen(false);
  };

  const handleProfileSuccess = () => {
    setShowProfileModal(false);
  };

  const handleProfileCancel = () => {
    setShowProfileModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <div 
              className="fixed inset-0 bg-black bg-opacity-50" 
              onClick={() => setSidebarOpen(false)} 
            />
            <motion.div
              variants={sidebarVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="relative flex w-80 flex-col bg-white dark:bg-gray-800 shadow-2xl"
            >
              {/* Mobile sidebar header */}
              <div className="flex h-16 items-center justify-between px-6 bg-gradient-to-r from-blue-600 to-blue-700">
                <div className="flex items-center space-x-3">
                  <Shield className="h-8 w-8 text-white" />
                  <h1 className="text-xl font-bold text-white">Admin Portal</h1>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="text-white hover:text-blue-200 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              {/* Mobile navigation */}
              <nav className="flex-1 px-4 py-6 space-y-2">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  const isExpanded = expandedMenus[item.name];
                  
                  if (item.hasSubmenu) {
                    return (
                      <div key={item.name}>
                        <button
                          onClick={() => toggleMenu(item.name)}
                          className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-xl hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 transition-all duration-200 group"
                        >
                          <div className="flex items-center">
                            <Icon className="mr-3 h-5 w-5 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                            {item.name}
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        {isExpanded && (
                          <div className="ml-6 space-y-1 mt-2">
                            {item.submenu.map((subItem) => {
                              const SubIcon = subItem.icon;
                              return (
                                <Link
                                  key={subItem.name}
                                  to={subItem.href}
                                  className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 transition-all duration-200"
                                >
                                  <SubIcon className="mr-3 h-4 w-4" />
                                  {subItem.name}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="flex items-center px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-xl hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 transition-all duration-200 group"
                    >
                      <Icon className="mr-3 h-5 w-5 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
              
              {/* Mobile user section */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-white">
                      {user?.fullName?.charAt(0) || 'A'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{user?.fullName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role?.toLowerCase()}</p>
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-72'}`}>
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-lg">
          {/* Desktop sidebar header */}
          <div className="flex items-center justify-between h-16 px-4 bg-gradient-to-r from-blue-600 to-blue-700">
            {!sidebarCollapsed && (
              <div className="flex items-center space-x-3">
                <img src={logo} alt="KirBOO Logo" className="w-8 h-8 rounded-lg object-cover" />
                <div>
                  <h1 className="text-lg font-bold text-white">KirBOO Flowsafe</h1>
                  <p className="text-xs text-blue-200">Admin Dashboard</p>
                </div>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 text-white hover:text-blue-200 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
          
          {/* Desktop navigation */}
          <nav className="flex-1 px-2 py-6 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isExpanded = expandedMenus[item.name];
              
              if (item.hasSubmenu) {
                if (sidebarCollapsed) {
                  return (
                    <div key={item.name} className="relative group">
                      <button
                        className="flex items-center justify-center w-full px-3 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-xl hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 transition-all duration-200"
                        title={item.name}
                      >
                        <Icon className="h-5 w-5" />
                      </button>
                      {/* Tooltip for collapsed submenu */}
                      <div className="absolute left-full top-0 ml-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                        {item.name}
                        <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-800"></div>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-xl hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 transition-all duration-200 group"
                    >
                      <div className="flex items-center">
                        <Icon className="mr-3 h-5 w-5 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                        {item.name}
                      </div>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="ml-6 space-y-1 mt-2">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 transition-all duration-200"
                            >
                              <SubIcon className="mr-3 h-4 w-4" />
                              {subItem.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-xl hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 transition-all duration-200 group ${
                    sidebarCollapsed ? 'justify-center' : ''
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <Icon className={`h-5 w-5 group-hover:text-blue-600 dark:group-hover:text-blue-400 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                  {!sidebarCollapsed && item.name}
                </Link>
              );
            })}
          </nav>
          
          {/* Desktop user section */}
          <div className="p-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            {!sidebarCollapsed ? (
              <>
                <div className="flex items-center space-x-3 mb-4 px-2">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-white">
                      {user?.fullName?.charAt(0) || 'A'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{user?.fullName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role?.toLowerCase()}</p>
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-xs font-semibold text-white">
                    {user?.fullName?.charAt(0) || 'A'}
                  </span>
                </div>
                <button
                  onClick={logout}
                  className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`flex flex-col h-screen transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-72'}`}>
        {/* Top navbar */}
        <div className="sticky top-0 z-40 flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm transition-colors duration-300">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            {/* Left side - Hamburger, dynamic icon and title */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors lg:hidden"
              >
                <Menu className="h-6 w-6" />
              </button>
              
              {/* Dynamic Page Icon */}
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <HeaderIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{pageHeaderConfig.title}</h1>
                  {/* <p className="text-sm text-gray-500 dark:text-gray-400">{pageHeaderConfig.subtitle}</p> */}
                </div>
              </div>
            </div>

            {/* Right side - Notifications and profile */}
            <div className="flex items-center space-x-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>

              {/* Notifications */}
              <button className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors relative">
                <Bell className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
              </button>

              {/* Profile dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center overflow-hidden">
                    {user?.profilePicture ? (
                      <img 
                        src={`http://localhost:8080/api/public/uploads/profile-pictures/${user.profilePicture}`}
                        alt="Profile" 
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-sm font-semibold text-white">
                        {user?.fullName?.charAt(0) || 'A'}
                      </span>
                    )}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.fullName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role?.toLowerCase()}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                </button>

                {/* Profile dropdown menu */}
                <AnimatePresence>
                  {profileDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
                    >
                      <button
                        onClick={handleProfileClick}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <User className="mr-3 h-4 w-4" />
                        Profile
                      </button>
                      <a
                        href="/admin/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <Settings className="mr-3 h-4 w-4" />
                        Settings
                      </a>
                      <hr className="my-1 border-gray-200 dark:border-gray-700" />
                      <button
                        onClick={logout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <LogOut className="mr-3 h-4 w-4" />
                        Sign out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto">
                  <div className="h-full">
                    <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 transition-colors duration-300">
                      {children}
                    </div>
                  </div>
                </main>
      </div>

      {/* Click outside to close dropdown */}
      {profileDropdownOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setProfileDropdownOpen(false)}
        />
      )}

      {/* Profile Modal */}
      <Modal
        isOpen={showProfileModal}
        onClose={handleProfileCancel}
        title="Admin Profile"
        size="xlarge"
      >
        <AdminProfile
          onSuccess={handleProfileSuccess}
          onCancel={handleProfileCancel}
          showHeader={false}
        />
      </Modal>
    </div>
  );
};

export default AdminLayout;
